import {
  CardContent,
  Card,
  CardActions,
  Button,
  Box,
  Stepper,
  StepLabel,
  Step,
} from '@mui/material';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { styled } from '@mui/material/styles';
import { getControlId } from '@veeva-ctv/utils';
import { Form, Formik, FormikConfig, FormikHelpers, FormikValues, useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { useMultiStepFormContext } from '@/context/MultiStepFormContext';

const StyledConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.secondary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.secondary.main,
    },
  },
}));

interface MultiStepFormProps extends FormikConfig<FormikValues> {
  children: React.ReactNode;
  isSubmitting?: boolean;
  displayStepper?: boolean;
  lastStepCTALabel?: string;
}

/* inspired by https://github.com/jaredpalmer/formik/blob/master/examples/MultistepWizard.js 
and modified to fit our usecase
*/
const MultiStepForm = ({
  children,
  onSubmit,
  initialValues,
  isSubmitting = false,
  displayStepper = true,
  lastStepCTALabel = 'Sign Up',
}: MultiStepFormProps) => {
  const formContext = useMultiStepFormContext();
  const steps = React.Children.toArray(children) as React.ReactElement<
    FormikConfig<FormikValues> & { name: string }
  >[];

  const step = steps[formContext?.activeStep as number];
  const totalSteps = steps.length;
  const isLastStep = formContext?.activeStep === totalSteps - 1;

  useEffect(() => {
    formContext?.setFormSnapshot(initialValues);
  }, [])

  const handleSubmit = async (values: FormikValues, helpers: FormikHelpers<FormikValues>) => {
    //if individual step has its own submit, call it
    if (step.props.onSubmit) {
      await step.props.onSubmit(values, helpers);
    }

    //if it's the last step, then call the top level form submit
    if (isLastStep) {
      return onSubmit(values, helpers);
    } else {
      void helpers.setTouched({});
      formContext?.nextFormStep(values);
      setTimeout(() => {
        void helpers.validateForm(values);
      }, 0);
    }
  };

  return (
    <div>
      <Formik
        initialValues={formContext!.formSnapshot as FormikValues}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnMount
        validationSchema={step.props.validationSchema}>
        {(formik) => (
          <>
            {displayStepper && (
              <Box mb={2}>
                <Stepper activeStep={formContext?.activeStep} connector={<StyledConnector />}>
                  {steps.map((step, idx) => (
                    <Step
                      key={step.props.name}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (formContext!.activeStep > idx) {
                          formContext!.setFormSnapshot(formik.values);
                          formContext!.setActiveStep(idx);
                          setTimeout(() => {
                            formik.setErrors({});
                            void formik.setTouched({});
                          }, 0);
                        }
                      }}>
                      <StepLabel>{step.props.name}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}

            <Card
              variant="outlined"
              sx={{ borderRadius: 1, border: (theme) => `2px solid ${theme.palette.grey[200]}` }}>
              <Form>
                <CardContent sx={{ p: '24px !important' }}>
                  {step}
                  <CardActions sx={{ px: 0 }}>
                    {formContext!.activeStep > 0 && (
                      <Button
                        variant="text"
                        onClick={() => {
                          formContext!.prevFormStep(formik.values);
                          setTimeout(() => {
                            formik.setErrors({});
                            void formik.setTouched({});
                          }, 0);
                        }}
                        sx={(theme) => ({ color: theme.palette.secondary.main })}
                        fullWidth>
                        Back
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      type="submit"
                      id={getControlId({ type: 'button', name: 'continue' })}
                      disabled={!formik.isValid || isSubmitting}>
                      {isLastStep ? lastStepCTALabel : 'Continue'}
                    </Button>
                  </CardActions>
                </CardContent>
              </Form>
            </Card>
          </>
        )}
      </Formik>
    </div>
  );
};

export default MultiStepForm;

export const FormStep = ({
  children,
}: {
  children: (formik) => JSX.Element;
  name: string;
  validationSchema;
  onSubmit?: (values: FormikValues) => Promise<any> | void;
}) => {
  const formik = useFormikContext();
  return children(formik);
};
